import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { AiOutlineButton } from '../ai';
import { ActivateAddonDialog } from '../activateAddonDialog/activateAddonDialog';
import { useDialogManager } from '../dialog';
import Stack from '../stack/stack';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';
import { AgentVectorizerCreditsDialog } from './AgentVectorizerCreditsDialog';

const Controls = styled.fieldset`
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
`;

interface AgentAddonActionCardProps {
    addonKey: string;
    action?: string | null;
    busy: boolean;
    onResume: () => void | Promise<void>;
    /** AI Studio activation also grants its first credit pack, so it uses its existing preflight. */
    onActivate?: () => void;
}

export const AgentAddonActionCard: FC<AgentAddonActionCardProps> = ({ addonKey, action, busy, onResume, onActivate }) => {
    const { subscription, sellerMarketplace, updateSubscription, activateAddonForSeller } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const { showDialog, closeDialog } = useDialogManager();
    const refresh = useRef(updateSubscription);
    refresh.current = updateSubscription;

    useEffect(() => {
        const onFocus = () => refresh.current();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    const addonName =
        addonKey === 'LARGE_FILES'
            ? T._('Large files', 'Admin')
            : addonKey === 'AI_STUDIO'
              ? T._('AI Studio', 'Admin')
              : addonKey === 'VECTORIZE'
                ? T._('AI Vectorizer', 'Admin')
                : addonKey.replace(/_/g, ' ');
    const hasAddon = subscription?.addons.some((addon) => addon.name === addonKey);
    // The requested action is supplied by Subscription, rather than inferred from a feature or entity.
    const planSupportsAddon = action !== 'upgrade_plan';
    const enabled = hasAddon && !action;
    const canManageSubscription = isAllowed(['SUBSCRIPTION']);

    const resume = () => {
        refresh.current();
        void onResume();
    };

    const activate = () => {
        if (addonKey === 'VECTORIZE') {
            const dialogID = 'agent-vectorizer-credits';
            showDialog(dialogID, <AgentVectorizerCreditsDialog onClose={() => closeDialog(dialogID)} onReady={resume} />);
            return;
        }
        if (onActivate) {
            onActivate();
            return;
        }
        showDialog(
            `agent-activate-${addonKey.toLowerCase()}`,
            <ActivateAddonDialog
                addonKey={addonKey}
                addonName={addonName}
                message={stringFormat(T._('Activate the {0} add-on to continue this configuration.', 'Admin'), addonName)}
                onActivateCompleted={resume}
            />
        );
    };

    return (
        <Controls disabled={busy}>
            <Stack $gap='12px'>
                {enabled ? (
                    <span>{stringFormat(T._('{0} is active. Resume to review the configuration before applying it.', 'Admin'), addonName)}</span>
                ) : !canManageSubscription ? (
                    <span>
                        {stringFormat(
                            T._('The {0} add-on is required. Ask the account owner or a user with subscription permissions to activate it.', 'Admin'),
                            addonName
                        )}
                    </span>
                ) : !planSupportsAddon ? (
                    <UpgradeDialog
                        type='BANNER'
                        marketPlace={sellerMarketplace ?? undefined}
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        showUpgradeButtonAsLink
                        addonKeyToActivate={addonKey}
                        addonNameToActivate={addonName}
                        buttonUpgradeText={T._('Upgrade & Add', 'Admin')}
                        message={stringFormat(
                            T._('Your plan does not support {0}. Upgrade your plan and activate the add-on to continue. Your draft is saved.', 'Admin'),
                            addonName
                        )}
                        onUpgradeCompleted={async () => {
                            activateAddonForSeller(addonKey, subscription?.plan?.currencyCode ?? 'USD', resume);
                        }}
                    />
                ) : (
                    <>
                        <span>
                            {stringFormat(
                                action === 'purchase_credits'
                                    ? T._('Purchase credits for {0} to continue. Your draft is saved.', 'Admin')
                                    : T._('Activate the {0} add-on to continue. Your draft is saved.', 'Admin'),
                                addonName
                            )}
                        </span>
                        <AiOutlineButton type='button' $size='small' disabled={busy} onClick={activate}>
                            {action === 'purchase_credits' ? T._('Purchase credits', 'Admin') : stringFormat(T._('Activate {0}', 'Admin'), addonName)}
                        </AiOutlineButton>
                    </>
                )}
                {(enabled || !onActivate) && (
                    <AiOutlineButton type='button' $size='small' disabled={busy} onClick={resume}>
                        {enabled ? T._('Resume configuration', 'Admin') : T._('Check add-on availability', 'Admin')}
                    </AiOutlineButton>
                )}
            </Stack>
        </Controls>
    );
};
